export type RecorderState = 'idle' | 'recording' | 'stopped' | 'error';

export type RecorderEvents = {
  onState?: (state: RecorderState) => void;
  onAmplitude?: (rms: number) => void;
  onTimeUpdate?: (seconds: number) => void;
  onMaxDuration?: () => void;
  onError?: (err: Error) => void;
};

const MAX_DURATION_SECONDS = 5 * 60;

export function pickMimeType(): { mime: string; ext: 'webm' | 'm4a' } {
  if (typeof MediaRecorder === 'undefined') {
    return { mime: 'audio/mp4', ext: 'm4a' };
  }
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
    return { mime: 'audio/webm;codecs=opus', ext: 'webm' };
  }
  if (MediaRecorder.isTypeSupported('audio/webm')) {
    return { mime: 'audio/webm', ext: 'webm' };
  }
  return { mime: 'audio/mp4', ext: 'm4a' };
}

export class AudioRecorder {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private chunks: Blob[] = [];
  private rafId: number | null = null;
  private startedAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private state: RecorderState = 'idle';
  private mimePicked: { mime: string; ext: 'webm' | 'm4a' } = pickMimeType();

  constructor(private events: RecorderEvents = {}) {}

  get currentState(): RecorderState {
    return this.state;
  }

  get mime(): string {
    return this.mimePicked.mime;
  }

  get ext(): 'webm' | 'm4a' {
    return this.mimePicked.ext;
  }

  async start(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      this.mimePicked = pickMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: this.mimePicked.mime,
        audioBitsPerSecond: 32000,
      });

      this.chunks = [];
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.mediaRecorder.onerror = (ev) => {
        const err = (ev as unknown as { error?: Error }).error;
        this.events.onError?.(err ?? new Error('MediaRecorder error'));
      };
      this.mediaRecorder.start(250);

      this.audioCtx = new AudioContext();
      const source = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 1024;
      source.connect(this.analyser);
      this.tickAmplitude();

      this.startedAt = performance.now();
      this.timer = setInterval(() => {
        const elapsed = (performance.now() - this.startedAt) / 1000;
        this.events.onTimeUpdate?.(elapsed);
        if (elapsed >= MAX_DURATION_SECONDS) {
          this.events.onMaxDuration?.();
          void this.stop();
        }
      }, 200);

      this.setState('recording');
    } catch (err) {
      this.cleanup();
      this.setState('error');
      this.events.onError?.(err as Error);
      throw err;
    }
  }

  async stop(): Promise<{ blob: Blob; duration_seconds: number; mime: string; ext: 'webm' | 'm4a' }> {
    if (!this.mediaRecorder || this.state !== 'recording') {
      throw new Error('not recording');
    }
    const recorder = this.mediaRecorder;
    const result = await new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => {
        try {
          resolve(new Blob(this.chunks, { type: this.mimePicked.mime }));
        } catch (err) {
          reject(err as Error);
        }
      };
      recorder.stop();
    });
    const duration_seconds = Math.max(1, Math.round((performance.now() - this.startedAt) / 1000));
    this.cleanup();
    this.setState('stopped');
    return { blob: result, duration_seconds, mime: this.mimePicked.mime, ext: this.mimePicked.ext };
  }

  cancel(): void {
    if (this.mediaRecorder && this.state === 'recording') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // ignore
      }
    }
    this.cleanup();
    this.setState('idle');
  }

  private cleanup(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // ignore
      }
      this.analyser = null;
    }
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      void this.audioCtx.close();
    }
    this.audioCtx = null;
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    this.mediaRecorder = null;
  }

  private setState(s: RecorderState): void {
    this.state = s;
    this.events.onState?.(s);
  }

  private tickAmplitude(): void {
    if (!this.analyser) return;
    const buffer = new Float32Array(this.analyser.fftSize);
    const tick = () => {
      if (!this.analyser || this.state !== 'recording') return;
      this.analyser.getFloatTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) sum += buffer[i]! * buffer[i]!;
      const rms = Math.sqrt(sum / buffer.length);
      this.events.onAmplitude?.(rms);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }
}
